export class TokenManager {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private storageType: 'localStorage' | 'sessionStorage';
  private refreshTimer: NodeJS.Timeout | null = null;
  private tokenRefreshCallbacks: Array<() => void> = [];
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor(storageType: 'localStorage' | 'sessionStorage' = 'sessionStorage') {
    this.storageType = storageType;
    this.loadTokens();
    this.setupAutoRefresh();
    this.setupStorageListener();
  }

  private get storage(): Storage {
    return this.storageType === 'localStorage' ? localStorage : sessionStorage;
  }

  private setupStorageListener(): void {
    if (this.storageType === 'localStorage') {
      window.addEventListener('storage', (e) => {
        if (e.key === 'accessToken' || e.key === 'refreshToken') {
          this.loadTokens();
          this.setupAutoRefresh();
        }
      });
    }
  }

  storeTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.storage.setItem('accessToken', accessToken);
    this.storage.setItem('refreshToken', refreshToken);
    this.setupAutoRefresh();
  }

  loadTokens(): void {
    this.accessToken = this.storage.getItem('accessToken');
    this.refreshToken = this.storage.getItem('refreshToken');
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.storage.removeItem('accessToken');
    this.storage.removeItem('refreshToken');
    this.clearAutoRefresh();
  }

  isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    
    try {
      const payload = this.parseJwt(token);
      const now = Date.now() / 1000;
      return payload.exp < now;
    } catch {
      return true;
    }
  }

  getTokenExpiry(token: string): Date | null {
    try {
      const payload = this.parseJwt(token);
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  private parseJwt(token: string): { exp: number; iat?: number; sub?: string; [key: string]: unknown } {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  }

  private setupAutoRefresh(): void {
    this.clearAutoRefresh();

    if (!this.accessToken) return;

    try {
      const payload = this.parseJwt(this.accessToken);
      const expiresIn = payload.exp * 1000 - Date.now();
      const refreshIn = Math.max(0, expiresIn - 60000);

      if (refreshIn > 0) {
        this.refreshTimer = setTimeout(() => {
          this.notifyTokenRefresh();
        }, refreshIn);
      } else if (expiresIn > 0) {
        this.notifyTokenRefresh();
      }
    } catch (error) {
      console.error('Failed to setup auto-refresh:', error);
    }
  }

  private clearAutoRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  onTokenRefresh(callback: () => void): () => void {
    this.tokenRefreshCallbacks.push(callback);
    return () => {
      const index = this.tokenRefreshCallbacks.indexOf(callback);
      if (index > -1) {
        this.tokenRefreshCallbacks.splice(index, 1);
      }
    };
  }

  private notifyTokenRefresh(): void {
    this.tokenRefreshCallbacks.forEach(callback => callback());
  }

  setRefreshing(promise: Promise<string> | null): void {
    this.isRefreshing = promise !== null;
    this.refreshPromise = promise;
  }

  getRefreshPromise(): Promise<string> | null {
    return this.refreshPromise;
  }

  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  shouldRefreshToken(): boolean {
    if (!this.accessToken) return false;
    
    try {
      const payload = this.parseJwt(this.accessToken);
      const expiresIn = payload.exp * 1000 - Date.now();
      return expiresIn < 60000;
    } catch {
      return false;
    }
  }

  rotateRefreshToken(newRefreshToken: string): void {
    this.refreshToken = newRefreshToken;
    this.storage.setItem('refreshToken', newRefreshToken);
  }
}

export const tokenManager = new TokenManager(
  (import.meta.env.VITE_TOKEN_STORAGE || 'sessionStorage') as 'localStorage' | 'sessionStorage'
);